import type { APICatalogFilter } from '~/api/catalogApi'
import type {
  TFilterItem,
  TFilterItemCategory,
  TFilterItemGroupedMultiselect,
  TFilterItemMultiselect,
  TFilterItemRange,
} from '~/layers/catalog/data/types'

import { useCachedStore } from '~/layers/cached/store/cached'
import { TFilterItemType } from '~/layers/catalog/data/types'
import { useCatalogStore } from '~/layers/catalog/store/catalog'

export const fromCatalogApi = (item: APICatalogFilter, dataType: CatalogDataProviderType): TFilterItem => {
  const type = defineFilterType(item)
  const cachedStore = useCachedStore()

  const getTooltip = (code: string) => {
    if (!cachedStore.tooltip || !cachedStore.tooltip[dataType]) {
      return null
    }

    return cachedStore.tooltip[dataType].find((t) => t.code === 'tooltip_' + code)?.tooltipText || null
  }

  let tooltip = getTooltip(item.code)

  if (dataType === 'jewelry' && !tooltip) {
    if (item.code === 'metal_colors') {
      tooltip = getTooltip('metal')
    }
    if (item.code === 'weight') {
      tooltip = getTooltip('insertsWeight')
    }
    if (item.code === 'sizes') {
      tooltip = getTooltip('size')
    }
  }

  return {
    title: item.title,
    type,
    code: item.code,
    isOpened: true,
    disabled: item.disabled === true,
    showInvestmentDiamonds: dataType !== 'jewelry' && (item.code === 'insert_weight' || item.code === 'weight'),
    showParametersRussianMetricSystem: item.hasRuValues,
    tooltip,
    applyingOrder: item.urlSort,
    ...make(item, type),
  }
}

const defineFilterType = (item: APICatalogFilter): TFilterItem['type'] => {
  if (item.code === 'categories') {
    return TFilterItemType.Category
  }

  if (item.variants.length === 2 && item.variants[0].code === 'min' && item.variants[1].code === 'max') {
    return TFilterItemType.Range
  }

  if (item.variants.find((i) => !!i.groupName)) {
    return TFilterItemType.MultiselectGrouped
  }

  return TFilterItemType.Multiselect
}

const make = (item: APICatalogFilter, type: TFilterItem['type']) => {
  if (type === TFilterItemType.Category) {
    return makeCategories(item)
  }

  if (type === TFilterItemType.Range) {
    return makeRange(item)
  }

  if (type === TFilterItemType.MultiselectGrouped) {
    return makeGroupedMultiselect(item)
  }

  if (type === TFilterItemType.Multiselect) {
    return makeMultiselect(item)
  }

  throw new Error(`Unknown type ${type}`)
}

const makeCategories = (item: APICatalogFilter): TFilterItemCategory => {
  useCatalogStore().setCategories(item.variants.map((i) => ({ name: i.name, code: i.code })))

  return {
    items: [
      { code: null, disabled: false, name: 'Все украшения' },
      ...item.variants.map((i) => ({ code: i.code, disabled: i.disabled === true, name: i.name })),
    ],
  }
}

const makeMultiselect = (item: APICatalogFilter): TFilterItemMultiselect => {
  return {
    items: item.variants.map((i) => ({
      name: i.name + '',
      code: i.code + '',
      disabled: i.disabled === true,
      isRuValue: i.isRuValue || false,
    })),
  }
}

const makeRange = (item: APICatalogFilter): TFilterItemRange => {
  return {
    range: {
      from: Number(item.variants[0].name || 0),
      to: Number(item.variants[1].name || 0),
    },
  }
}

const makeGroupedMultiselect = (item: APICatalogFilter): TFilterItemGroupedMultiselect => {
  const groups: TFilterItemGroupedMultiselect['groups'] = []

  for (const variant of item.variants) {
    const groupName = variant.groupName || null
    const findGroup = groups.find((g) => g.name === groupName)

    if (findGroup) {
      findGroup.items.push({
        name: variant.name + '',
        code: variant.code + '',
        disabled: variant.disabled === true,
        isRuValue: variant.isRuValue || false,
      })
    } else {
      groups.push({
        name: groupName,
        items: [
          {
            name: variant.name + '',
            code: variant.code + '',
            disabled: variant.disabled === true,
            isRuValue: variant.isRuValue || false,
          },
        ],
      })
    }
  }

  return { groups: groups.filter((i) => i.items.length) }
}
